import { createBrowserRouter, Navigate, Outlet, useLocation } from "react-router-dom";
import type { UserRole } from "@app/shared";
import { useAuth } from "./auth-context";
import { AppLayout, PublicLayout } from "./layout";
import { Loading } from "../components/states";

// 公開ページ
import { PublicCompanyListPage } from "../features/companies/PublicCompanyListPage";
import { PublicCompanyDetailPage } from "../features/companies/PublicCompanyDetailPage";
import { PublicMapPage } from "../features/companies/PublicMapPage";
import { MapSearchPage } from "../features/companies/MapSearchPage";
import { CompanyRegisterPage } from "../features/companies/CompanyRegisterPage";
import { LoginPage } from "../features/auth/LoginPage";
import { ForgotPasswordPage } from "../features/auth/ForgotPasswordPage";
import { ResetPasswordPage } from "../features/auth/ResetPasswordPage";
import { SecurityPage } from "../features/auth/SecurityPage";
// 学校
import { SchoolSearchPage } from "../features/companies/SchoolSearchPage";
import { SchoolApplicationsPage } from "../features/applications/SchoolApplicationsPage";
import { BulkApplyPage } from "../features/applications/BulkApplyPage";
// 企業
import { CompanyProfilePage } from "../features/companies/CompanyProfilePage";
import { CompanyInboxPage } from "../features/applications/CompanyInboxPage";
// 管理
import { AdminDashboardPage } from "../features/admin/AdminDashboardPage";
import { AdminReviewsPage } from "../features/admin/AdminReviewsPage";
import { AdminCompanyCreatePage } from "../features/admin/AdminCompanyCreatePage";
import { AdminSchoolsPage } from "../features/admin/AdminSchoolsPage";
import { AdminIndustriesPage } from "../features/admin/AdminIndustriesPage";

// ロール保護（セキュリティはサーバの authz が担保。ここはUX目的・[19] §5）
function Protected({ roles }: { roles: UserRole[] }) {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  if (isLoading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (!roles.includes(user.role)) return <Navigate to="/app" replace />;
  // 管理者は二要素認証が必須（未登録ならセキュリティ設定へ誘導・F-11/[19] §6.7）
  if (
    user.role === "admin" &&
    !user.twoFactorEnabled &&
    !location.pathname.startsWith("/app/security")
  ) {
    return <Navigate to="/app/security" replace />;
  }
  return (
    <AppLayout>
      <Outlet />
    </AppLayout>
  );
}

// /app に来たらロール別ホームへ
function AppHome() {
  const { user, isLoading } = useAuth();
  if (isLoading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  const home: Record<UserRole, string> = {
    school: "/app/school/search",
    company: "/app/company/profile",
    admin: "/app/admin",
  };
  return <Navigate to={home[user.role]} replace />;
}

function Public({ children }: { children: React.ReactNode }) {
  return <PublicLayout>{children}</PublicLayout>;
}

export const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <Public>
        <PublicCompanyListPage />
      </Public>
    ),
  },
  {
    path: "/map",
    element: (
      <Public>
        <PublicMapPage />
      </Public>
    ),
  },
  {
    path: "/map/search",
    element: (
      <Public>
        <MapSearchPage />
      </Public>
    ),
  },
  {
    path: "/companies/:id",
    element: (
      <Public>
        <PublicCompanyDetailPage />
      </Public>
    ),
  },
  {
    path: "/register/company",
    element: (
      <Public>
        <CompanyRegisterPage />
      </Public>
    ),
  },
  {
    path: "/login",
    element: (
      <Public>
        <LoginPage />
      </Public>
    ),
  },
  {
    path: "/forgot-password",
    element: (
      <Public>
        <ForgotPasswordPage />
      </Public>
    ),
  },
  {
    path: "/reset-password",
    element: (
      <Public>
        <ResetPasswordPage />
      </Public>
    ),
  },

  { path: "/app", element: <AppHome /> },

  // セキュリティ設定（全ロール・2要素認証エンロール）
  {
    element: <Protected roles={["admin", "school", "company"]} />,
    children: [{ path: "/app/security", element: <SecurityPage /> }],
  },

  // 学校
  {
    element: <Protected roles={["school"]} />,
    children: [
      { path: "/app/school/search", element: <SchoolSearchPage /> },
      { path: "/app/school/bulk-apply", element: <BulkApplyPage /> },
      { path: "/app/school/applications", element: <SchoolApplicationsPage /> },
    ],
  },
  // 企業
  {
    element: <Protected roles={["company"]} />,
    children: [
      { path: "/app/company/profile", element: <CompanyProfilePage /> },
      { path: "/app/company/inbox", element: <CompanyInboxPage /> },
    ],
  },
  // 管理
  {
    element: <Protected roles={["admin"]} />,
    children: [
      { path: "/app/admin", element: <AdminDashboardPage /> },
      { path: "/app/admin/reviews", element: <AdminReviewsPage /> },
      { path: "/app/admin/companies/new", element: <AdminCompanyCreatePage /> },
      { path: "/app/admin/schools", element: <AdminSchoolsPage /> },
      { path: "/app/admin/industries", element: <AdminIndustriesPage /> },
    ],
  },

  { path: "*", element: <Navigate to="/" replace /> },
]);
